import type { RouteLocationNormalized, RouteRecordNormalized } from 'vue-router';
import { APP_NAVIGATION_PRIMARY, APP_NAVIGATION_SECONDARY } from '@/config/navigation';
import { filterNavigationItems } from '@/views/layouts/app/navigation/filter-navigation-items';
import type { NavigationItem } from '@/types';

/**
 * Traverse route tree up, return the first found route under the
 * `matched` key where `matchedRoute.meta` has a property value.
 */
export const getRouteTreeProperty = <T>(
  route: RouteLocationNormalized,
  property: string
): T | false => {
  const matched: RouteRecordNormalized[] = Object.assign([], route?.matched);
  const foundRoute: RouteRecordNormalized | undefined = matched
    .reverse()
    .find((r: RouteRecordNormalized) => r.meta.hasOwnProperty(property));

  if (foundRoute) return foundRoute.meta[property];

  return false;
};

/**
 * Traverse route tree up, return an array of matched
 * `route.meta` property values. Good to use when
 * you want all matching `meta.key` values
 */
export const getDeepRouteTreeProperty = <T>(
  route: RouteLocationNormalized,
  property: string
): T[] | false => {
  const matched: RouteRecordNormalized[] = Object.assign([], route?.matched);
  const foundRoutes: T[] = matched
    .reverse()
    .filter((r: RouteRecordNormalized) => r.meta.hasOwnProperty(property))
    .map((r) => r.meta[property]);

  if (foundRoutes?.length) return foundRoutes;

  return false;
};

/**
 * Walk a (privilege-filtered) navigation tree and return the route name
 * of the first navigable item found, descending into groups.
 */
const findFirstNavigableRouteName = (items: NavigationItem[]): string | undefined => {
  for (const item of items) {
    if (item.routeName) return item.routeName;

    for (const group of item.groups ?? []) {
      const found = findFirstNavigableRouteName(group.children);
      if (found) return found;
    }
  }

  return undefined;
};

/**
 * Returns the route name of the first item in the main app navigation
 * the given privileges grant access to, or undefined if none do.
 */
export const getFirstAccessibleNavigationRouteName = (userPrivileges: string[]): string | undefined => {
  const accessible = filterNavigationItems(userPrivileges, [
    ...APP_NAVIGATION_PRIMARY,
    ...APP_NAVIGATION_SECONDARY,
  ]);

  return findFirstNavigableRouteName(accessible);
};

/**
 * Parse a redirect URL to extract path and query parameters
 * for use with Vue Router navigation
 */
export const parseRedirectUrl = (redirectUrl: string): { path: string; query: Record<string, string> } => {
  const url = new URL(redirectUrl, window.location.origin);
  const queryParams: Record<string, string> = {};
  url.searchParams.forEach((value, key) => {
    queryParams[key] = value;
  });

  return {
    path: url.pathname,
    query: queryParams
  };
};
